"use client";
/**
 * Streaks widget for the rewards hub (design 1.7). Renders the four states
 * the design specifies — active / at-risk / milestone / broken — derived from
 * the backend's `{count, lastPeriodKey}` representation via `deriveStreakView`.
 *
 * Hidden by the caller when streaks are disabled (the hook reports
 * `streaksEnabled: false`).
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Card } from "@/components/ui";
import type { StreakView } from "@/hooks/useLoyaltyExpansion";

/** How many cells to draw in the streak ladder. */
const LADDER = 5;

export function StreakWidget({ view }: { view: StreakView }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const labelKey =
    view.type === "weeklyVisit"
      ? "v2.loyalty_hub.streak_weekly"
      : "v2.loyalty_hub.streak_monthly";
  const title = t(labelKey, { count: view.count });

  if (view.state === "milestone") {
    return (
      <Card
        padding={14}
        style={{
          overflow: "hidden",
          position: "relative",
          background: "linear-gradient(135deg, var(--bonus-bg) 0%, #ffe7c4 100%)",
          border: "1px solid #f4d99c",
        }}
      >
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          aria-hidden
          style={{ position: "absolute", insetInlineEnd: -6, top: -6, opacity: 0.6 }}
        >
          {(
            [
              [10, 40, "#e8552d"],
              [50, 8, "#0baa57"],
              [70, 28, "#c9a449"],
              [28, 12, "#0baa57"],
              [58, 46, "#e8552d"],
              [18, 58, "#c9a449"],
            ] as [number, number, string][]
          ).map(([x, y, c], i) => (
            <circle key={i} cx={x} cy={y} r={2.5} fill={c} />
          ))}
        </svg>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              display: "grid",
              placeItems: "center",
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "var(--gold-bg)",
              color: "var(--gold-fg)",
              flexShrink: 0,
            }}
          >
            <Icons.trophy size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 14.5, color: "#7a4815" }}>
              {t("v2.loyalty_hub.streak_milestone_title", { count: view.count })}
            </div>
            <div style={{ fontSize: 12, marginTop: 1, color: "#7a4815" }}>
              {t("v2.loyalty_hub.streak_milestone_sub")}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (view.state === "at_risk") {
    return (
      <Card padding={14} style={{ borderInlineStart: "3px solid #f59e0b" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              display: "grid",
              placeItems: "center",
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "var(--bonus-bg)",
              color: "var(--bonus)",
              flexShrink: 0,
            }}
          >
            <Icons.fire size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 14.5 }}>
              {title}
            </div>
            <div style={{ fontSize: 12, marginTop: 1, color: "var(--bonus)", fontWeight: 600 }}>
              {t("v2.loyalty_hub.streak_at_risk")}
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            style={{
              height: 36,
              padding: "0 14px",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: "var(--radius-btn)",
              background: "var(--primary-600)",
              color: "#fff",
              border: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              flexShrink: 0,
            }}
          >
            {t("v2.loyalty_hub.order_now")}
          </button>
        </div>
      </Card>
    );
  }

  if (view.state === "broken") {
    return (
      <Card padding={14}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              display: "grid",
              placeItems: "center",
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "var(--surface-2)",
              color: "var(--ink-2)",
              filter: "grayscale(1)",
              flexShrink: 0,
            }}
          >
            <Icons.fire size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 14.5, color: "var(--ink-2)" }}>
              {t("v2.loyalty_hub.streak_broken_title")}
            </div>
            <div style={{ fontSize: 12, marginTop: 1, color: "var(--ink-3)" }}>
              {t("v2.loyalty_hub.streak_broken_sub")}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // active
  const reached =
    view.nextMilestoneCount != null
      ? Math.max(0, view.nextMilestoneCount - view.count)
      : null;
  return (
    <Card padding={14}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            width: 40,
            height: 40,
            borderRadius: 12,
            background: "var(--flame-bg)",
            color: "var(--flame)",
            flexShrink: 0,
          }}
        >
          <Icons.fire size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="display" style={{ fontSize: 15 }}>
            {title}
          </div>
          {reached != null && reached > 0 && (
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              {t("v2.loyalty_hub.streak_next_milestone", { count: reached })}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${LADDER}, 1fr)`,
          gap: 6,
          marginTop: 14,
        }}
      >
        {Array.from({ length: LADDER }, (_, i) => i + 1).map((step) => {
          const filled = step <= Math.min(view.count, LADDER);
          const isNext = step === Math.min(view.count, LADDER) + 1;
          return (
            <div
              key={step}
              style={{
                height: 36,
                borderRadius: 10,
                background: filled ? "var(--flame-bg)" : "var(--surface-2)",
                border: isNext ? "1.5px dashed var(--flame)" : "1px solid transparent",
                display: "grid",
                placeItems: "center",
                color: filled ? "var(--flame)" : "var(--ink-3)",
                fontSize: 10.5,
                fontWeight: 700,
              }}
            >
              {filled ? <Icons.fire size={14} /> : <span>{step}</span>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
